import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "../contexts/PermissionContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { UserCog, LogIn, LogOut, Lock, Shield, User as UserIcon } from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";

const API = window.location.origin;

const ROLE_LABELS = {
  cashier: { label: "كاشير", color: "text-blue-400", emoji: "🛒" },
  salesman: { label: "مندوب مبيعات", color: "text-emerald-400", emoji: "💼" },
  inventory_manager: { label: "أمين مخزن", color: "text-amber-400", emoji: "📦" },
  accountant: { label: "محاسب", color: "text-purple-400", emoji: "🧮" },
  manager: { label: "مدير", color: "text-pink-400", emoji: "👔" },
  admin: { label: "مسؤول", color: "text-red-400", emoji: "🛡️" },
};

export default function ShiftSwitcher() {
  const { token, user } = useAuth();
  const { activeEmployee, isOwner, isSubAccount, startShift, endShift } = usePermissions();
  const [open, setOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [step, setStep] = useState("list"); // list | pin
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && token) loadEmployees();
  }, [open, token]);

  // ✨ Multi-User: لو الحساب موظف (sub-account) مسجل دخول مباشرة بـ JWT
  // ما نعرض ShiftSwitcher أبداً - بياناته تظهر من Sidebar/Header
  // (الوردية القديمة تخص حساب المالك بس)
  if (isSubAccount) {
    return null;
  }

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployees(res.data || []);
    } catch {
      toast.error("فشل تحميل الموظفين");
    } finally {
      setLoading(false);
    }
  };

  const pickEmployee = (emp) => {
    setSelectedEmployee(emp);
    setPin("");
    setStep("pin");
  };

  const verifyPinAndStart = () => {
    if (!selectedEmployee) return;
    const expected = String(selectedEmployee.pin || selectedEmployee.password || "").trim();
    if (expected && pin.trim() !== expected) {
      toast.error("الرمز السري غير صحيح");
      return;
    }
    startShift(selectedEmployee);
    toast.success(`بدأت وردية ${selectedEmployee.name}`);
    resetAndClose();
  };

  const handleEndShift = () => {
    if (window.confirm("هل تريد إنهاء وردية الموظف الحالي والعودة لوضع المالك؟")) {
      endShift();
      toast.success("تم إنهاء الوردية");
    }
  };

  const resetAndClose = () => {
    setStep("list");
    setSelectedEmployee(null);
    setPin("");
    setOpen(false);
  };

  const role = activeEmployee ? ROLE_LABELS[activeEmployee.role_id] : null;

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            data-testid="shift-switcher-trigger"
            className={`h-9 gap-1.5 px-2.5 text-xs ${
              isOwner
                ? "bg-slate-800/80 border-emerald-500/30 hover:border-emerald-500 text-emerald-400"
                : "bg-amber-500/10 border-amber-500/40 hover:border-amber-500 text-amber-400"
            }`}
          >
            {isOwner ? (
              <>
                <Shield className="w-3.5 h-3.5" />
                <span className="hidden sm:inline font-bold">المالك</span>
              </>
            ) : (
              <>
                <UserIcon className="w-3.5 h-3.5" />
                <span className="font-bold truncate max-w-[80px]">{activeEmployee.name}</span>
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent dir="rtl" align="end" className="w-72 bg-slate-900 border-slate-700 text-white p-4 space-y-3">
          {isOwner ? (
            <>
              <div className="flex items-center gap-2 pb-2 border-b border-slate-700">
                <Shield className="w-4 h-4 text-emerald-500" />
                <div>
                  <p className="text-sm font-bold">أنت تستخدم الحساب كصاحب محل</p>
                  <p className="text-[11px] text-slate-400">صلاحيات كاملة</p>
                </div>
              </div>
              <Button
                onClick={() => setOpen(true)}
                size="sm"
                data-testid="start-shift-btn"
                className="w-full bg-amber-600 hover:bg-amber-700"
              >
                <UserCog className="w-4 h-4 ml-2" />
                بدء وردية موظف
              </Button>
              <p className="text-[10px] text-slate-500 leading-relaxed">
                💡 ابدأ وردية موظف عند تسليم الجهاز للكاشير. كل العمليات راح تُسجّل باسمه ويشوف بس الصفحات المسموحة له.
              </p>
            </>
          ) : (
            <>
              <div className="flex items-start gap-2 pb-2 border-b border-slate-700">
                <span className="text-2xl">{role?.emoji || "👤"}</span>
                <div className="flex-1">
                  <p className="text-sm font-bold">{activeEmployee.name}</p>
                  <p className={`text-[11px] ${role?.color || "text-slate-400"}`}>
                    {role?.label || "موظف"}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    بدأ: {new Date(activeEmployee.started_at).toLocaleTimeString("ar-IQ", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setOpen(true)}
                size="sm"
                variant="outline"
                className="w-full border-slate-600 text-slate-200 hover:bg-slate-800"
                data-testid="switch-shift-btn"
              >
                <UserCog className="w-4 h-4 ml-2" />
                تبديل لموظف آخر
              </Button>
              <Button
                onClick={handleEndShift}
                size="sm"
                variant="destructive"
                className="w-full"
                data-testid="end-shift-btn"
              >
                <LogOut className="w-4 h-4 ml-2" />
                إنهاء الوردية والعودة للمالك
              </Button>
            </>
          )}
        </PopoverContent>
      </Popover>

      <Dialog open={open} onOpenChange={(v) => { if (!v) resetAndClose(); else setOpen(v); }}>
        <DialogContent dir="rtl" className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              {step === "list" ? (
                <>
                  <UserCog className="w-5 h-5 text-emerald-500" />
                  اختر الموظف لبدء الوردية
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5 text-amber-500" />
                  أدخل الرمز السري
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {step === "list" && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {loading ? (
                <p className="text-slate-400 text-center py-6 text-sm">جاري التحميل...</p>
              ) : employees.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-slate-400 text-sm mb-2">ما عدك موظفين مسجلين بعد</p>
                  <p className="text-[11px] text-slate-500">روح لـ "الموظفين" بالقائمة لإضافة موظف وتحديد رمزه السري</p>
                </div>
              ) : (
                employees.map((emp) => {
                  const r = ROLE_LABELS[emp.role_id] || ROLE_LABELS.cashier;
                  return (
                    <button
                      key={emp.id}
                      onClick={() => pickEmployee(emp)}
                      data-testid={`select-employee-${emp.id}`}
                      className="w-full flex items-center gap-3 p-3 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-emerald-500/50 transition-all text-right"
                    >
                      <span className="text-2xl">{r.emoji}</span>
                      <div className="flex-1">
                        <p className="font-bold">{emp.name}</p>
                        <p className={`text-xs ${r.color}`}>{r.label}</p>
                      </div>
                      <LogIn className="w-4 h-4 text-emerald-500" />
                    </button>
                  );
                })
              )}
            </div>
          )}

          {step === "pin" && (
            <div className="space-y-4">
              <div className="text-center py-3 bg-slate-800/50 rounded-lg">
                <p className="text-2xl mb-1">{ROLE_LABELS[selectedEmployee?.role_id]?.emoji || "👤"}</p>
                <p className="font-bold">{selectedEmployee?.name}</p>
                <p className={`text-xs ${ROLE_LABELS[selectedEmployee?.role_id]?.color || "text-slate-400"}`}>
                  {ROLE_LABELS[selectedEmployee?.role_id]?.label || "موظف"}
                </p>
              </div>
              <div>
                <Label className="text-slate-300 text-sm">الرمز السري (PIN)</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && verifyPinAndStart()}
                  placeholder="••••"
                  className="mt-1 bg-slate-800 border-slate-700 text-white text-center text-2xl tracking-[0.5em]"
                  autoFocus
                  data-testid="shift-pin-input"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  💡 الرمز معد من قبل صاحب المحل بصفحة "الموظفين". إذا الموظف ما عنده رمز، اتركه فارغاً.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={verifyPinAndStart}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="confirm-pin-btn"
                >
                  <LogIn className="w-4 h-4 ml-2" />
                  بدء الوردية
                </Button>
                <Button
                  onClick={() => setStep("list")}
                  variant="outline"
                  className="border-slate-600 text-slate-300"
                >
                  رجوع
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
